import type {
  IGroupedOptions,
  ISelectionOption,
} from '@components/Pages/AnalyticsPage/analytics.types';
import type { ISiteProfession } from '@apis/professions';

/**
 * tradeId → display name overrides for cases where the camelCase →
 * sentence-case fallback in `formatTradeName` produces a wart (acronyms
 * or unusual capitalisation). Mirrors trusstor's `shared/translations`
 * en-us values for the most-seen trades; unknown tradeIds fall through
 * to the default formatter.
 */
const TRADE_NAME_OVERRIDES: Record<string, string> = {
  hvac: 'HVAC',
  mep: 'MEP',
  dryWall: 'Dry Wall',
  generalConstruction: 'General Construction',
  generalWorks: 'General Works',
  fireProtection: 'Fire Protection',
  hoistOperator: 'Hoist Operator',
  // Trusstor's `guest` trade translates to "Visitor" (en-us) — the guest
  // tag class is presented to users as visitors everywhere in the source UI.
  guest: 'Visitor',
  visitor: 'Visitor',
};

/**
 * tradeGroup key → human-friendly group header. Keys come from trusstor's
 * profession records (`construction`, `general`, `structure`, `systems`,
 * `others`). Unknown groups fall through to `Other`.
 */
const TRADE_GROUP_NAMES: Record<string, string> = {
  construction: 'Construction',
  general: 'General',
  structure: 'Structure',
  systems: 'Systems',
  foundations: 'Foundations',
  finishing: 'Finishing',
  exterior: 'Exterior',
  interior: 'Interior',
  others: 'Other',
};

/**
 * camelCase → sentence-case fallback for tradeIds we don't have an
 * override for. `aluminum` → "Aluminum", `carpentry` → "Carpentry",
 * `glazier` → "Glazier", `plumbing` → "Plumbing".
 */
function camelToSentence(s: string): string {
  if (!s) return '';
  const spaced = s.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatTradeName(tradeId: string): string {
  return TRADE_NAME_OVERRIDES[tradeId] ?? camelToSentence(tradeId);
}

export function formatTradeGroup(tradeGroup: string | null): string {
  if (!tradeGroup) return 'Other';
  return TRADE_GROUP_NAMES[tradeGroup] ?? camelToSentence(tradeGroup);
}

/**
 * Build the canonical user-facing label for a profession option, mirroring
 * trusstor's `getProfessionDisplayText`:
 *
 *   "{tradeName}, {specialty} - {contractor}"
 *
 * Each segment is optional — drop it cleanly if absent so we never render
 * dangling punctuation.
 */
export function buildProfessionLabel(p: ISiteProfession): string {
  const trade = formatTradeName(p.tradeId);
  let label = trade;
  if (p.specialty) label += `, ${p.specialty}`;
  if (p.contractor) label += ` - ${p.contractor}`;
  return label;
}

/**
 * Project the BFF `ISiteProfession[]` into the `IGroupedOptions[]` shape
 * that `GroupedSelect` consumes — one group per `tradeGroup` (display name
 * via `formatTradeGroup`), each option carrying chip metadata so the
 * dropdown renders the trusstor-style coloured pill + contractor row.
 *
 * Groups are emitted in a stable, alphabetical order — the BFF sorts the
 * raw list by `tradeGroup`, so reading the buckets in insertion order
 * preserves that order without re-sorting on the client.
 */
export function professionsToGroupedOptions(
  professions: ISiteProfession[],
): IGroupedOptions[] {
  const buckets = new Map<string, ISelectionOption[]>();
  for (const p of professions) {
    const groupId = p.tradeGroup ?? 'others';
    const options = buckets.get(groupId) ?? [];
    options.push({
      id: p.id,
      label: buildProfessionLabel(p),
      chip: {
        background: p.backgroundColor ?? '#576270',
        text: p.specialty ?? formatTradeName(p.tradeId),
        tradeId: p.tradeId,
        ...(p.contractor !== null && { contractor: p.contractor }),
      },
    });
    buckets.set(groupId, options);
  }
  return Array.from(buckets.entries()).map(([groupId, options]) => ({
    groupId,
    groupLabel: formatTradeGroup(groupId === 'others' ? null : groupId),
    options,
  }));
}
